import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { BRAND } from "@/lib/brand";
import { getRecentActivities } from "@/lib/recentActivities";
import { Clock as ClockIcon } from "lucide-react";

// Drop-in replacement for the activity <input> on the clock widget. Same
// visual styling; adds a typeahead dropdown sourced from the freelancer's
// own recent activities. Keyboard:
//   ↑/↓  navigate suggestions
//   Enter pick the highlighted suggestion (or submit if none highlighted —
//          parent's onSubmit handles that via the surrounding <form>)
//   Esc  close the dropdown
//
// Falls through to behaving exactly like a regular input when the user has
// no past activities or the network call fails.
const ActivityInput = forwardRef(function ActivityInput(
  {
    value,
    onChange,
    placeholder,
    disabled,
    maxLength = 500,
    testId = "timer-activity-input",
    style,
    className,
  },
  ref
) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapperRef = useRef(null);

  // Load suggestions once when the field first gets focus — avoids a network
  // round-trip on initial dashboard render for users who never type here.
  const ensureLoaded = () => {
    if (suggestions.length || open === false) {
      // already populated, or nothing requested yet
    }
    getRecentActivities().then((list) => {
      setSuggestions(list);
    });
  };

  const filtered = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    if (!q) return suggestions.slice(0, 8);
    return suggestions
      .filter((s) => s.toLowerCase().includes(q) && s.toLowerCase() !== q)
      .slice(0, 8);
  }, [suggestions, value]);

  // Click-away closes the dropdown.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pick = (s) => {
    onChange?.(s);
    setOpen(false);
    setHighlight(-1);
  };

  const onKeyDown = (e) => {
    if (!open || filtered.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? filtered.length - 1 : h - 1));
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      pick(filtered[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setHighlight(-1);
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <input
        ref={ref}
        type="text"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onFocus={() => {
          ensureLoaded();
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        data-testid={testId}
        disabled={disabled}
        maxLength={maxLength}
        className={
          className ||
          "flex-1 px-3.5 py-2.5 rounded-lg border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 transition w-full"
        }
        style={{ "--tw-ring-color": BRAND.teal, ...(style || {}) }}
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden"
          data-testid="activity-suggestions"
        >
          <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 bg-slate-50 border-b border-slate-100">
            Recent
          </div>
          <ul role="listbox">
            {filtered.map((s, i) => (
              <li key={s}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    // mousedown rather than click so we beat the input's blur
                    e.preventDefault();
                    pick(s);
                  }}
                  onMouseEnter={() => setHighlight(i)}
                  data-testid={`activity-suggestion-${i}`}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition ${
                    highlight === i ? "bg-slate-100" : "hover:bg-slate-50"
                  } text-slate-700`}
                >
                  <ClockIcon size={12} className="text-slate-400 shrink-0" />
                  <span className="truncate">{s}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
});

export default ActivityInput;
